import React, { useState, useRef, useEffect } from 'react';
import {
  Alert,
  ChatBubbleIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  MinusIcon,
  CpuChipIcon,
  UserIcon,
} from '../ui';
import Logger from '../../utils/logger';
import { AgentUpdate } from '../../services/agent/AgentService';

interface FloatingChatProps {
  messages: AgentUpdate[];
  onSendMessage: (message: string) => Promise<void>;
  isSending: boolean;
  isLoading: boolean;
  error?: string;
  disabled?: boolean;
  currentCaseTitle?: string;
}

const logger = Logger.create('FloatingChat');

const FloatingChat: React.FC<FloatingChatProps> = ({
  messages,
  onSendMessage,
  isSending,
  isLoading,
  error,
  disabled = false,
  currentCaseTitle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Count unread messages when chat is closed
  useEffect(() => {
    if (!isOpen) {
      const recentMessages = messages.filter((msg) => {
        const msgTime = new Date(msg.timestamp).getTime();
        const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
        return msgTime > fiveMinutesAgo && msg.source !== 'USER';
      });
      setUnreadCount(recentMessages.length);
    } else {
      setUnreadCount(0);
    }
  }, [messages, isOpen]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && chatInputRef.current) {
      setTimeout(() => chatInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleToggleChat = () => {
    setIsOpen(!isOpen);
    setUnreadCount(0);
  };

  const handleSendMessage = async () => {
    if (!message.trim() || isSending || disabled) return;

    try {
      await onSendMessage(message);
      setMessage('');
    } catch (error) {
      logger.error('Error sending message:', error);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Floating Chat Container */}
      <div
        className={`fixed bottom-5 right-5 z-[1000] transition-all duration-300 ${
          isOpen ? 'w-[500px] h-[500px]' : 'w-auto h-auto'
        }`}
      >
        {/* Expanded Chat Panel */}
        {isOpen && (
          <div
            className="w-[500px] h-[500px] flex flex-col rounded-xl overflow-hidden border border-secondary-200 shadow-elevated bg-white"
            role="dialog"
            aria-modal="true"
            aria-labelledby="chat-title"
            aria-describedby="chat-description"
          >
            {/* Chat Header */}
            <div className="bg-primary-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <CpuChipIcon
                  className="w-5 h-5 flex-shrink-0"
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <h2 id="chat-title" className="font-semibold text-base">
                    Agent Chat
                  </h2>
                  {currentCaseTitle && (
                    <p
                      id="chat-description"
                      className="text-xs opacity-90 truncate"
                    >
                      {currentCaseTitle}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={handleToggleChat}
                  className="p-1.5 rounded hover:bg-primary-700 transition-colors"
                  aria-label="Minimize chat"
                >
                  <MinusIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={handleToggleChat}
                  className="p-1.5 rounded hover:bg-primary-700 transition-colors"
                  aria-label="Close chat"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div
              className="flex-1 overflow-y-auto p-2 bg-secondary-50 flex flex-col gap-2"
              role="log"
              aria-live="polite"
              aria-label="Chat messages"
            >
              {messages.length > 0 ? (
                messages.map((msg, index) => (
                  <div
                    key={`${msg.timestamp}-${index}`}
                    className={`max-w-[75%] ${
                      msg.source === 'USER' ? 'self-end' : 'self-start'
                    }`}
                  >
                    <div
                      className={`p-3 rounded-lg shadow-sm ${
                        msg.source === 'USER'
                          ? 'bg-primary-600 text-white'
                          : 'bg-white text-secondary-900'
                      }`}
                    >
                      <div className="flex gap-2 items-start mb-1">
                        {msg.source === 'USER' ? (
                          <UserIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        ) : (
                          <CpuChipIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <span className="text-xs opacity-80 block">
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </span>
                          <p className="text-sm whitespace-pre-wrap break-words mt-1">
                            {typeof msg.content === 'string'
                              ? msg.content
                              : JSON.stringify(msg.content, null, 2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <CpuChipIcon className="w-12 h-12 text-secondary-400 mx-auto mb-2" />
                  <p className="text-secondary-500 text-sm">
                    {disabled
                      ? 'Navigate to a specific business case to start chatting with the agent'
                      : 'Start a conversation with the agent'}
                  </p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Divider */}
            <div className="h-px bg-secondary-200" />

            {/* Input Area */}
            <div className="p-4 bg-white">
              {error && (
                <Alert
                  variant="error"
                  className="mb-2 text-sm"
                  role="alert"
                  aria-live="assertive"
                >
                  {error}
                </Alert>
              )}
              <div className="flex gap-2 items-end">
                <textarea
                  ref={chatInputRef}
                  className="flex-1 px-3 py-2 border border-secondary-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-secondary-100 disabled:cursor-not-allowed"
                  placeholder={
                    disabled
                      ? 'Select a business case to chat...'
                      : 'Type your message...'
                  }
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyPress}
                  disabled={isSending || isLoading || disabled}
                  rows={1}
                  style={{ maxHeight: '80px' }}
                  aria-label="Chat message input"
                  aria-describedby={error ? 'chat-error' : undefined}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={
                    !message.trim() || isSending || isLoading || disabled
                  }
                  className="p-2.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:bg-secondary-300 disabled:cursor-not-allowed transition-colors"
                  aria-label="Send message"
                >
                  <PaperAirplaneIcon className="w-5 h-5" />
                </button>
              </div>
              {(isSending || isLoading) && (
                <p
                  className="text-xs text-secondary-500 mt-1"
                  aria-live="polite"
                >
                  {isSending ? 'Sending message...' : 'Processing...'}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Floating Action Button */}
        {!isOpen && (
          <div className="relative">
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-error-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center z-10">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
            <button
              onClick={handleToggleChat}
              disabled={disabled}
              className="w-14 h-14 rounded-full bg-primary-600 text-white shadow-lg hover:shadow-xl hover:scale-105 disabled:bg-secondary-400 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
              aria-label={
                unreadCount > 0
                  ? `Open chat (${unreadCount} unread messages)`
                  : 'Open chat with agent'
              }
              aria-expanded={isOpen}
            >
              <ChatBubbleIcon className="w-7 h-7" />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default FloatingChat;
